import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { User, Mail, Building, Phone, Globe, Shield, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useUpdateProviderCustomer } from '@/hooks/provider/useProviderCustomers';

const editCustomerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  company: z.string().optional(),
  phone: z.string().optional(),
  timezone: z.string().default('UTC'),
  status: z.enum(['active', 'inactive', 'suspended']),
  twoFactorEnabled: z.boolean().default(false),
});

type EditCustomerFormData = z.infer<typeof editCustomerSchema>;

interface EditCustomerDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
    phone?: string;
    timezone: string;
    status: 'active' | 'inactive' | 'suspended';
    role: 'owner' | 'member' | 'individual';
    twoFactorEnabled: boolean;
  };
}

export function EditCustomerDialog({ open, onOpenChange, customer }: EditCustomerDialogProps) {
  const { t } = useTranslation();
  const updateCustomer = useUpdateProviderCustomer();

  const form = useForm<EditCustomerFormData>({
    resolver: zodResolver(editCustomerSchema),
    defaultValues: {
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email,
      company: customer.company || '',
      phone: customer.phone || '',
      timezone: customer.timezone,
      status: customer.status,
      twoFactorEnabled: customer.twoFactorEnabled,
    },
  });

  const timezoneOptions = useMemo(
    () => [
      { value: 'UTC', label: t('timezones.utc', { defaultValue: 'UTC' }) },
      { value: 'America/New_York', label: t('timezones.americaNewYork', { defaultValue: 'America/New_York' }) },
      { value: 'America/Chicago', label: t('timezones.americaChicago', { defaultValue: 'America/Chicago' }) },
      { value: 'America/Los_Angeles', label: t('timezones.americaLosAngeles', { defaultValue: 'America/Los_Angeles' }) },
      { value: 'Europe/London', label: t('timezones.europeLondon', { defaultValue: 'Europe/London' }) },
      { value: 'Europe/Berlin', label: t('timezones.europeBerlin', { defaultValue: 'Europe/Berlin' }) },
      { value: 'Asia/Tokyo', label: t('timezones.asiaTokyo', { defaultValue: 'Asia/Tokyo' }) },
      { value: 'Australia/Sydney', label: t('timezones.australiaSydney', { defaultValue: 'Australia/Sydney' }) },
    ],
    [t],
  );

  useEffect(() => {
    if (customer) {
      form.reset({
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        company: customer.company || '',
        phone: customer.phone || '',
        timezone: customer.timezone,
        status: customer.status,
        twoFactorEnabled: customer.twoFactorEnabled,
      });
    }
  }, [customer, form]);

  async function onSubmit(data: EditCustomerFormData) {
    try {
      await updateCustomer.mutateAsync({
        id: customer.id,
        firstName: data.firstName,
        lastName: data.lastName,
        company: data.company,
        phone: data.phone,
        timezone: data.timezone,
        status: data.status,
        twoFactorEnabled: data.twoFactorEnabled,
      });
      toast.success(t('customers.edit.success', 'Customer updated'));
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : t('customers.edit.error', 'Failed to update customer');
      toast.error(message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('customers.edit.title')}</DialogTitle>
          <DialogDescription>
            {t('customers.edit.description')}
          </DialogDescription>
        </DialogHeader>

        {customer.role !== 'individual' && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {customer.role === 'owner' 
                ? t('customers.edit.ownerWarning')
                : t('customers.edit.memberWarning')
              }
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customers.edit.fields.firstName')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input {...field} className="pl-9" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customers.edit.fields.lastName')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers.edit.fields.email')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input {...field} type="email" className="pl-9" disabled />
                      </div>
                    </FormControl>
                    <FormDescription>
                      {t('customers.edit.fields.emailNote')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customers.edit.fields.company')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input {...field} className="pl-9" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customers.edit.fields.phone')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input {...field} className="pl-9" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="timezone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers.edit.fields.timezone')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {timezoneOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers.edit.fields.status')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-green-500" />
                            <span>{t('customers.status.active')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="inactive">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-gray-500" />
                            <span>{t('customers.status.inactive')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="suspended">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-red-500" />
                            <span>{t('customers.status.suspended')}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {t('customers.edit.fields.statusDescription')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="twoFactorEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        {t('customers.edit.fields.twoFactor')}
                      </FormLabel>
                      <FormDescription>
                        {t('customers.edit.fields.twoFactorDescription')}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateCustomer.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={updateCustomer.isPending}>
                {updateCustomer.isPending ? t('common.saving') : t('customers.edit.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default EditCustomerDialog;
